/**
 * Product Card V2 Slider
 * Transform-based slider with pointer-events dragging
 * Uses GPU-accelerated transforms to avoid rendering artifacts
 */

interface SliderSettings {
  enableNavigation?: boolean;
  enableDrag?: boolean;
}

interface BreakpointSettings {
  slidesPerView?: number;
}

export default class ProductCardV2Slider extends HTMLElement {
  private container: HTMLElement | null = null;
  private track: HTMLElement | null = null;
  private slideEls: HTMLElement[] = [];
  
  // Settings & State
  private settings: SliderSettings = {};
  private breakpoints: Record<string, BreakpointSettings> = {};
  private activeIdx: number = 0;
  private slideWidth: number = 0;
  private maxIndex: number = 0;
  
  // Navigation
  private prevBtns: HTMLElement[] = [];
  private nextBtns: HTMLElement[] = [];
  private paginationContainer: HTMLElement | null = null;
  
  // Product Link
  private productUrl: string | null = null;

  // Transform State
  private currentX: number = 0;
  private targetX: number = 0;
  
  // Pointer Drag State
  private isDown: boolean = false;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartTranslateX: number = 0;
  private lastPointerX: number = 0;
  private lastPointerTime: number = 0;
  private velocity: number = 0;
  private pointerId: number | null = null;
  
  // Timers
  private animationId: number | null = null;
  private resizeHandler: (() => void) | null = null;

  constructor() {
    super();
    
    this.settings = {
      enableNavigation: true,
      enableDrag: true
    };
    
    const settingsAttr = this.getAttribute('data-settings');
    if (settingsAttr) {
      try {
        this.settings = { ...this.settings, ...JSON.parse(settingsAttr) };
      } catch (e) {
        console.warn('Invalid JSON in data-settings');
      }
    }

    const breakpointsAttr = this.getAttribute('data-breakpoints');
    if (breakpointsAttr) {
      try {
        this.breakpoints = JSON.parse(breakpointsAttr);
      } catch (e) {
        console.warn('Invalid JSON in data-breakpoints');
      }
    }
  }

  connectedCallback(): void {
    this.container = this.querySelector('.carousel-scroll');
    if (!this.container) return;

    this.slideEls = Array.from(this.querySelectorAll('.carousel-slide'));
    if (this.slideEls.length === 0) return;

    // Use container as track (the element that moves)
    this.track = this.container;

    this.paginationContainer = this.querySelector('.carousel-pagination');
    this.prevBtns = Array.from(this.querySelectorAll('.js-nav-btn-prev, [data-nav-prev]'));
    this.nextBtns = Array.from(this.querySelectorAll('.js-nav-btn-next, [data-nav-next]'));
    
    // Get product URL for click navigation
    this.productUrl = this.getAttribute('data-product-url');

    this.init();
  }

  disconnectedCallback(): void {
    this.cleanup();
  }

  private init(): void {
    if (!this.track) return;

    // Setup transform-based sliding
    this.setupStyles();
    
    // Calculate dimensions
    this.calculateDimensions();

    // Setup navigation
    if (this.settings.enableNavigation) {
      this.initNav();
    }

    // Setup drag
    if (this.settings.enableDrag) {
      this.initDrag();
    }

    // Resize listener
    this.resizeHandler = this.debounce(() => {
      this.calculateDimensions();
      this.goToSlide(this.activeIdx, false);
    }, 100);
    window.addEventListener('resize', this.resizeHandler);

    // Initial position
    this.goToSlide(0, false);
  }

  private setupStyles(): void {
    if (!this.track) return;
    
    // Disable native scrolling
    this.track.style.overflow = 'visible';
    this.track.style.scrollSnapType = 'none';
    
    // GPU acceleration
    this.track.style.willChange = 'transform';
    this.track.style.backfaceVisibility = 'hidden';
    this.track.style.transform = 'translate3d(1px, 0, 0)'; // Start from 1px to prevent sliver glitch
    
    // Ensure slides don't cause overflow issues
    this.slideEls.forEach(slide => {
      slide.style.backfaceVisibility = 'hidden';
      slide.style.transform = 'translate3d(0, 0, 0)';
    });
  }

  private calculateDimensions(): void {
    if (!this.track || this.slideEls.length === 0) return;

    // Get slide width from first slide
    const firstSlide = this.slideEls[0];
    this.slideWidth = firstSlide.offsetWidth;
    
    // Calculate max index based on breakpoints
    const currentWidth = window.visualViewport?.width || document.documentElement.clientWidth;
    const activeBreakpoint = Object.keys(this.breakpoints)
      .map(Number)
      .filter(bp => currentWidth >= bp)
      .sort((a, b) => b - a)[0] || 0;

    const config = this.breakpoints[activeBreakpoint.toString()] || {};
    const slidesPerView = config.slidesPerView || 1;
    
    // Max index = total slides - visible slides
    this.maxIndex = Math.max(0, this.slideEls.length - slidesPerView);
    
    // Clamp active index
    if (this.activeIdx > this.maxIndex) {
      this.activeIdx = this.maxIndex;
    }

    // Render pagination
    this.renderPaginationDots();
    this.updateButtonStates();
  }

  private cleanup(): void {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.track) {
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
    }
  }

  private initNav(): void {
    this.prevBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
    });

    this.nextBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    });
  }

  private renderPaginationDots(): void {
    if (!this.paginationContainer) return;

    const dotsCount = this.maxIndex + 1;
    this.paginationContainer.innerHTML = '';

    for (let i = 0; i < dotsCount; i++) {
      const btn = document.createElement('button');
      btn.className = `carousel-bullet ${this.activeIdx === i ? 'active' : ''}`;
      btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
      btn.dataset.index = i.toString();
      
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goToSlide(i, true);
      });

      this.paginationContainer.appendChild(btn);
    }
  }

  private updatePaginationDots(): void {
    if (!this.paginationContainer) return;
    
    const dots = Array.from(this.paginationContainer.children);
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.activeIdx);
    });
  }

  private updateButtonStates(): void {
    const isStart = this.activeIdx === 0;
    const isEnd = this.activeIdx >= this.maxIndex;

    this.prevBtns.forEach(btn => {
      if (isStart) btn.setAttribute('disabled', 'true');
      else btn.removeAttribute('disabled');
    });

    this.nextBtns.forEach(btn => {
      if (isEnd) btn.setAttribute('disabled', 'true');
      else btn.removeAttribute('disabled');
    });
  }

  private prev(): void {
    if (this.activeIdx > 0) {
      this.goToSlide(this.activeIdx - 1, true);
    }
  }

  private next(): void {
    if (this.activeIdx < this.maxIndex) {
      this.goToSlide(this.activeIdx + 1, true);
    }
  }

  private goToSlide(index: number, animate: boolean = true): void {
    if (!this.track) return;

    // Clamp index
    index = Math.max(0, Math.min(index, this.maxIndex));
    this.activeIdx = index;

    // Calculate target position
    this.targetX = -index * this.slideWidth;
    
    if (animate) {
      this.animateToTarget();
    } else {
      this.currentX = this.targetX;
      this.applyTransform(this.currentX);
    }

    this.updatePaginationDots();
    this.updateButtonStates();
  }

  private animateToTarget(): void {
    if (!this.track) return;
    
    // Use CSS transition for smooth animation
    this.track.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)';
    this.applyTransform(this.targetX);
    this.currentX = this.targetX;
    
    // Clear transition after animation
    setTimeout(() => {
      if (this.track) {
        this.track.style.transition = 'none';
      }
    }, 300);
  }

  private applyTransform(x: number): void {
    if (!this.track) return;
    // Round first, then subtract 1px per slide to prevent sliver glitch
    // Slide 0: 0 - 1 = -1 (wait no, we need +1 at 0)
    // Actually: position 0 needs to be 1, so we add 1 to all
    const roundedX = Math.round(x);
    // Shift by 1px to avoid rendering at exact 0 position
    const finalX = roundedX === 0 ? 1 : roundedX;
    // Use translate3d for GPU acceleration
    this.track.style.transform = `translate3d(${finalX}px, 0, 0)`;
  }

  // --- Drag Logic ---

  private initDrag(): void {
    if (!this.track) return;

    this.track.style.touchAction = 'pan-y pinch-zoom';
    this.track.style.userSelect = 'none';
    this.track.style.cursor = this.productUrl ? 'pointer' : 'grab';

    // Bind methods to preserve context
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());

    // Block clicks during drag
    this.addEventListener('click', (e) => {
      if (this.isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true });
    
    // Handle clean clicks to navigate to product page
    this.track.addEventListener('click', (e) => {
      // Skip if clicking on pagination, buttons, or links
      if ((e.target as HTMLElement).closest('.carousel-pagination, button, a')) return;
      
      // Only navigate if it wasn't a drag and we have a URL
      if (!this.isDragging && this.productUrl) {
        window.location.href = this.productUrl;
      }
    });
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.track) return;
    
    // Skip pagination/buttons
    if ((e.target as HTMLElement).closest('.carousel-pagination, button, a')) return;
    if (e.button !== 0) return;

    this.isDown = true;
    this.isDragging = false;
    this.pointerId = e.pointerId;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragStartTranslateX = this.currentX;
    this.lastPointerX = e.clientX;
    this.lastPointerTime = performance.now();
    this.velocity = 0;

    this.track.setPointerCapture(e.pointerId);
    this.track.style.transition = 'none';
    this.track.style.cursor = 'grabbing';
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.isDown || !this.track) return;
    if (e.pointerId !== this.pointerId) return;

    const x = e.clientX;
    const y = e.clientY;
    const dx = x - this.dragStartX;
    const dy = y - this.dragStartY;

    // Determine drag direction
    if (!this.isDragging) {
      const threshold = 5;
      if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
        if (Math.abs(dx) > Math.abs(dy)) {
          // Horizontal drag
          this.isDragging = true;
        } else {
          // Vertical - cancel and let browser handle
          this.isDown = false;
          this.track.releasePointerCapture(e.pointerId);
          this.track.style.cursor = 'grab';
          return;
        }
      }
    }

    if (this.isDragging) {
      e.preventDefault();

      // Calculate new position
      let newX = this.dragStartTranslateX + dx;
      
      // Calculate bounds
      const minX = -this.maxIndex * this.slideWidth;
      const maxX = 0;
      
      // Apply rubber band effect at boundaries
      if (newX > maxX) {
        newX = maxX + (newX - maxX) * 0.2;
      } else if (newX < minX) {
        newX = minX + (newX - minX) * 0.2;
      }

      this.currentX = newX;
      this.applyTransform(newX);

      // Track velocity
      const now = performance.now();
      const dt = now - this.lastPointerTime;
      if (dt > 0 && dt < 100) {
        const instantVelocity = (x - this.lastPointerX) / dt;
        this.velocity = this.velocity * 0.6 + instantVelocity * 0.4;
      }
      this.lastPointerX = x;
      this.lastPointerTime = now;
    }
  }

  private onPointerUp(e: PointerEvent): void {
    if (!this.isDown || !this.track) return;
    if (e.pointerId !== this.pointerId) return;

    this.isDown = false;
    this.pointerId = null;

    try {
      this.track.releasePointerCapture(e.pointerId);
    } catch {
      // Already released
    }

    this.track.style.cursor = this.productUrl ? 'pointer' : 'grab';

    if (this.isDragging) {
      this.handleDragEnd();
    }

    // Reset after a small delay to prevent click
    setTimeout(() => {
      this.isDragging = false;
    }, 10);
  }

  private handleDragEnd(): void {
    // Find nearest slide index
    let nearestIndex = Math.round(-this.currentX / this.slideWidth);
    
    // Apply velocity-based adjustment
    const velocityThreshold = 0.3;
    if (Math.abs(this.velocity) > velocityThreshold) {
      if (this.velocity < 0) {
        // Swiped left -> next
        nearestIndex = Math.ceil(-this.currentX / this.slideWidth);
      } else {
        // Swiped right -> prev
        nearestIndex = Math.floor(-this.currentX / this.slideWidth);
      }
    }

    // Clamp to valid range
    nearestIndex = Math.max(0, Math.min(nearestIndex, this.maxIndex));

    // Animate to position
    this.goToSlide(nearestIndex, true);
  }

  private debounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
    let timeoutId: number | null = null;
    return ((...args: unknown[]) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn(...args), delay);
    }) as T;
  }
}
